import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/context/auth";
import i18n from "@/i18n";
import type {
  Profile,
  ProfileResponse,
  BusinessProfileResponse,
} from "@/schemas";

import {
  getProfile,
  getUserProfile,
  updateProfile,
  getWorkerProfileForEmployer,
  getEmployerProfileForWorker,
} from "../api/profileApi";
import { QueryKeys } from "../queryKeys";
import { useCustomMutation, useCustomQuery } from "../queryUtils";
import { notify } from "@/utils";

export function useGetProfileQuery() {
  // Only fetch once the user is authenticated. Firing before login completes
  // sends a request with no Authorization header → a transient 401 (which then
  // self-heals via token refresh). Gating on auth state avoids the spurious 401.
  const { isAuthenticated } = useAuth();
  return useCustomQuery<ProfileResponse, Error>(
    [QueryKeys.PROFILE],
    getProfile,
    {
      staleTime: 60_000,
      enabled: isAuthenticated,
    },
  );
}

// Fetch any user's profile by userId
export function useGetUserProfileQuery(userId: string) {
  return useCustomQuery<ProfileResponse, Error>(
    [QueryKeys.PROFILE, userId],
    () => getUserProfile(userId),
  );
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  return useCustomMutation<ProfileResponse, Error, Partial<Profile>>(
    updateProfile,
    {
      onError: (error: Error) => {
        console.error("Update profile failed:", error);
        notify.error(
          i18n.t("personal.saveError", {
            ns: "settings",
            defaultValue: "Failed to update profile. Please try again.",
          }),
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.PROFILE] });
      },
    },
  );
}

// Employer -> fetch worker profile
export function useWorkerProfileForEmployer(userId: string) {
  return useCustomQuery<ProfileResponse, Error>(
    [QueryKeys.PUBLIC_USER_PROFILE, userId],
    () => getWorkerProfileForEmployer(userId),
    { staleTime: 60_000 },
  );
}

// Worker -> fetch employer/business profile
export function useEmployerProfileForWorker(businessId: number) {
  return useCustomQuery<BusinessProfileResponse, Error>(
    [QueryKeys.PUBLIC_USER_PROFILE, businessId],
    () => getEmployerProfileForWorker(businessId),
    { staleTime: 60_000 },
  );
}
